import { API_BASE } from "@/api/config";

// Mirrors apps/server/src/interview-demo. Plain fetch on purpose: the demo turns rate limits and
// capacity errors into its own states instead of global error toasts. The signed-in calls
// (results, consent, ask) take a Clerk session token.

export type EvidenceSource = "resume" | "spotlight" | "interview";

export type DemoEvidence = { label: string; text: string };

export type DemoCandidate = {
  id: string;
  name: string;
  headline: string;
  location: string;
  resume: DemoEvidence[];
  spotlight: DemoEvidence[];
  interview: { questionIndex: number; at: string; text: string }[];
};

export type DemoRole = {
  id: string;
  category: string;
  title: string;
  company: string;
  location: string;
  summary: string;
  mustHaves: string[];
  questions: [string, string, string];
  suggestedQuestions: string[];
  candidates: DemoCandidate[];
};

export type DemoStatus = { enabled: boolean; remaining: number; retryAfterSeconds: number };

export type DemoSession = {
  sessionId: string;
  serverUrl: string;
  token: string;
  maxSeconds: number;
  remaining: number;
};

export type TranscriptRow = {
  id: string;
  speaker: "agent" | "candidate";
  text: string;
  at: number;
};

export type DemoCitation = {
  id: string;
  candidateId: string;
  candidateName: string;
  source: EvidenceSource;
  label: string;
  text: string;
};

export type DemoAnswer = {
  answer: string;
  citations: DemoCitation[];
  remainingQuestions: number;
};

export class DemoApiError extends Error {
  constructor(readonly status: number, readonly code: string, readonly retryAfterSeconds?: number) {
    super(code);
  }
}

const request = async <T>(path: string, init?: RequestInit, token?: string | null): Promise<T> => {
  let response: Response;
  try {
    response = await fetch(`${API_BASE}/interview-demo${path}`, {
      ...init,
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...init?.headers,
      },
    });
  } catch {
    throw new DemoApiError(0, "network");
  }

  const body = await response.json().catch(() => null);
  if (!response.ok) {
    throw new DemoApiError(response.status, body?.code ?? "unknown", body?.retryAfterSeconds);
  }
  // A misrouted proxy can answer with HTML; treat that as unavailable, not as data.
  if (body === null || typeof body !== "object") throw new DemoApiError(502, "bad_response");
  return body as T;
};

export const fetchDemoRoles = () =>
  request<{ roles: DemoRole[] }>("/roles").then((data) => data.roles);

export const fetchDemoStatus = () => request<DemoStatus>("/status");

export const startDemoSession = (roleId: string) =>
  request<DemoSession>("/session", { method: "POST", body: JSON.stringify({ roleId }) });

// Called while the visitor is still in the room: the server checks the interviewer really is
// missing before giving the attempt back.
export const reportDemoNoAgent = (sessionId: string) =>
  request<{ refunded: boolean }>(`/session/${sessionId}/no-agent`, { method: "POST" });

/**
 * Called when the call ends: the results are shown on recruit.theresume.io after sign-in.
 * `replayUrl` is this visit's PostHog recording, which ends up on the CRM lead.
 */
export const saveDemoTranscript = (
  sessionId: string,
  transcript: TranscriptRow[],
  interviewSeconds: number,
  replayUrl?: string
) =>
  request<{ saved: boolean; expiresAt?: string }>(`/session/${sessionId}/transcript`, {
    method: "POST",
    body: JSON.stringify({
      transcript: transcript
        .slice(-80)
        .map((row) => ({
          speaker: row.speaker,
          text: row.text.trim().slice(0, 800),
          at: Math.min(Math.max(row.at, 0), 600),
        }))
        .filter((row) => row.text.length > 0),
      interviewSeconds: Math.min(Math.max(Math.round(interviewSeconds), 0), 600),
      replayUrl,
    }),
  });

export type DemoAttribution = {
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  landingPage?: string;
  // PostHog recording of this visit to the results page.
  replayUrl?: string;
};

export type DemoResults = {
  roleId: string;
  transcript: { speaker: TranscriptRow["speaker"]; text: string; at: number }[];
  interviewSeconds: number;
  remainingQuestions: number;
  marketingConsent: boolean;
  /** When the transcript is deleted (ISO time). */
  expiresAt?: string;
};

export type LatestDemo = { sessionId: string; roleId: string; expiresAt: string };

/** Signed in: the user's latest demo while it is kept (2 hours), or null. */
export const fetchLatestDemo = async (token: string) =>
  (await request<{ demo: LatestDemo | null }>("/mine", undefined, token)).demo;

/** Signed in: opens (and on first open, claims) the session's results. */
export const openDemoResults = (sessionId: string, attribution: DemoAttribution, token: string) =>
  request<DemoResults>(
    `/session/${sessionId}/results`,
    { method: "POST", body: JSON.stringify(attribution) },
    token
  );

export const setDemoConsent = (sessionId: string, marketingConsent: boolean, token: string) =>
  request<{ marketingConsent: boolean }>(
    `/session/${sessionId}/consent`,
    { method: "POST", body: JSON.stringify({ marketingConsent }) },
    token
  );

/** Signed in: the server answers from the transcript it saved for this session. */
export const askDemo = (
  payload: {
    sessionId: string;
    question: string;
    history: { role: "user" | "assistant"; content: string }[];
  },
  token: string
) => request<DemoAnswer>("/ask", { method: "POST", body: JSON.stringify(payload) }, token);
